import { LitElement, html, unsafeCSS } from "lit";
import { property } from "lit/decorators.js";
// Header/title/body + close button are styled by the Interstate slide-out/button
// Tailwind DIY recipes (tailwind.config.js plugins), not the ids-* web components.
import tailwindShared from "../../../styles/tailwind-shared.css?inline";
import tailwindComponents from "../../../styles/tailwind-components.css?inline";
import styles from "./SupportMenuSlideout.css?inline";

// App-owned slideout: a thin wrapper over the native Popover API (popover=auto →
// light-dismiss + a `toggle` event). Replaces <ids-slideout>; the host is the popover.
export class SupportMenuSlideout extends LitElement {
  static readonly styles = [
    unsafeCSS(tailwindShared),
    unsafeCSS(tailwindComponents),
    unsafeCSS(styles),
  ];

  // Reflected so :host([open]) and tests can observe it; syncs the popover state.
  @property({ type: Boolean, reflect: true }) open = false;

  @property() header = "Support";

  override connectedCallback(): void {
    super.connectedCallback();
    // Light-dismiss popover, dialog role (not modal), focusable. Set if not overridden.
    if (!this.hasAttribute("popover")) this.setAttribute("popover", "auto");
    if (!this.hasAttribute("role")) this.setAttribute("role", "dialog");
    if (!this.hasAttribute("tabindex")) this.setAttribute("tabindex", "-1");
    this.setAttribute("aria-label", this.header);
  }

  override updated(changed: Map<PropertyKey, unknown>): void {
    if (changed.has("open")) this._syncPopover();
    if (changed.has("header")) this.setAttribute("aria-label", this.header);
  }

  // showPopover/hidePopover throw if called in the wrong state; gate on the live
  // match. try/catch guards happy-dom's partial Popover API in the unit env.
  // ARGUS PR #164 (MAJOR): focus() is called HERE, right after showPopover()
  // actually runs — not by the parent (SupportMenuPanel) in the same update
  // cycle that sets `open`. Lit applies `?open=` to this child in a LATER
  // microtask than the parent's own updated(), so a parent-side focus() call
  // always raced ahead of showPopover(), landing while the popover was still
  // closed (`.focus()` on a closed [popover] is a no-op) — WCAG 2.4.3 regression.
  private _syncPopover(): void {
    const isOpen = this._isPopoverOpen();
    try {
      if (this.open && !isOpen) {
        this.showPopover?.();
        this.focus();
      } else if (!this.open && isOpen) {
        this.hidePopover?.();
      }
    } catch {
      /* best-effort in the unit env */
    }
  }

  private _isPopoverOpen(): boolean {
    try {
      return this.matches?.(":popover-open") ?? false;
    } catch {
      return false;
    }
  }

  // Hides the popover → `toggle` (closed) → SupportMenuPanel closes. Same path as Escape.
  private _onCloseClick(): void {
    try {
      if (this._isPopoverOpen()) this.hidePopover?.();
      else this.open = false;
    } catch {
      this.open = false;
    }
  }

  override render() {
    // Interstate slide-out DIY recipe: .ids-slideout(-right) drives the whole surface;
    // .support-menu-slideout-* are stable test/style hooks.
    return html`
      <div
        class="ids-slideout ids-slideout-right support-menu-slideout-surface"
      >
        <div class="ids-slideout-header">
          <span class="ids-slideout-title support-menu-slideout-title"
            >${this.header}</span
          >
          <button
            type="button"
            class="ids-btn support-menu-slideout-close"
            data-ids-variant="static"
            aria-label="Close ${this.header}"
            @click=${this._onCloseClick}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 16 16"
              width="16"
              height="16"
              fill="currentColor"
              aria-hidden="true"
              focusable="false"
            >
              <path
                d="M2.57637 2.57643C2.82045 2.33235 3.21617 2.33235 3.46025 2.57643L7.99994 7.11612L12.5396 2.57643C12.7837 2.33235 13.1794 2.33235 13.4235 2.57643C13.6676 2.82051 13.6676 3.21624 13.4235 3.46031L8.88382 8L13.4235 12.5397C13.6676 12.7838 13.6676 13.1795 13.4235 13.4236C13.1794 13.6676 12.7837 13.6676 12.5396 13.4236L7.99994 8.88388L3.46025 13.4236C3.21617 13.6676 2.82045 13.6676 2.57637 13.4236C2.33229 13.1795 2.33229 12.7838 2.57637 12.5397L7.11606 8L2.57637 3.46031C2.33229 3.21624 2.33229 2.82051 2.57637 2.57643Z"
              />
            </svg>
          </button>
        </div>
        <div class="ids-slideout-body"><slot></slot></div>
      </div>
    `;
  }
}

if (!customElements.get("support-menu-slideout")) {
  customElements.define("support-menu-slideout", SupportMenuSlideout);
}

declare global {
  interface HTMLElementTagNameMap {
    "support-menu-slideout": SupportMenuSlideout;
  }
}
